import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Plus, Trash2, ListOrdered, BookOpen, Info, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const AdminDropdownPage = () => {
  const [challengeValues, setChallengeValues] = useState([]);
  const [articleValues, setArticleValues] = useState([]);
  const [existingTitles, setExistingTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newChallenge, setNewChallenge] = useState('');
  const [newArticle, setNewArticle] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [cRes, aRes, titlesRes] = await Promise.all([
        api.get('/admin/dropdown-options/challenge'),
        api.get('/admin/dropdown-options/articles'),
        api.get('/article-titles-global').catch(() => ({ data: [] })),
      ]);
      setChallengeValues(cRes.data.values || []);
      setArticleValues(aRes.data.values || []);
      setExistingTitles(titlesRes.data || []);
    } catch { toast.error('فشل تحميل البيانات'); }
    finally { setLoading(false); }
  };

  const saveValues = async (type, values) => {
    try {
      await api.put(`/admin/dropdown-options/${type}`, { values });
      toast.success('تم الحفظ');
    } catch { toast.error('فشل الحفظ'); }
  };

  const addChallenge = () => {
    const val = parseInt(newChallenge);
    if (!val || val <= 0) { toast.error('أدخل رقماً صحيحاً'); return; }
    if (challengeValues.includes(val)) { toast.error('القيمة موجودة مسبقاً'); return; }
    const updated = [...challengeValues, val].sort((a, b) => a - b);
    setChallengeValues(updated);
    saveValues('challenge', updated);
    setNewChallenge('');
  };

  const removeChallenge = (val) => {
    const updated = challengeValues.filter(v => v !== val);
    setChallengeValues(updated);
    saveValues('challenge', updated);
  };

  const addArticle = () => {
    const val = newArticle.trim();
    if (!val) { toast.error('أدخل اسم المقالة'); return; }
    if (articleValues.includes(val)) { toast.error('المقالة موجودة مسبقاً'); return; }
    const updated = [...articleValues, val];
    setArticleValues(updated);
    saveValues('articles', updated);
    setNewArticle('');
  };

  const removeArticle = (val) => {
    const updated = articleValues.filter(v => v !== val);
    setArticleValues(updated);
    saveValues('articles', updated);
  };

  const startEditArticle = (idx) => {
    setEditingIdx(idx);
    setEditValue(articleValues[idx]);
  };

  const cancelEditArticle = () => {
    setEditingIdx(null);
    setEditValue('');
  };

  const saveEditArticle = () => {
    const val = editValue.trim();
    if (!val) { toast.error('لا يمكن أن يكون الاسم فارغاً'); return; }
    if (articleValues.includes(val) && val !== articleValues[editingIdx]) { toast.error('المقالة موجودة مسبقاً'); return; }
    const updated = [...articleValues];
    updated[editingIdx] = val;
    setArticleValues(updated);
    saveValues('articles', updated);
    setEditingIdx(null);
    setEditValue('');
  };

  // Merge admin-managed + existing auto-suggested titles
  const allArticleTitles = [...new Set([...articleValues, ...existingTitles])];

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8 p-4 sm:p-6 max-w-4xl mx-auto" data-testid="dropdown-management-page">
      <div>
        <h1 className="text-4xl font-bold text-accent">إدارة القوائم المنسدلة</h1>
        <p className="text-muted-foreground text-lg mt-1">إدارة قيم التحدي وقائمة المقالات في التقرير القرائي</p>
      </div>

      {/* Challenge Values */}
      <Card className="p-6 space-y-4" data-testid="challenge-section">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 rounded-full bg-amber-100">
            <ListOrdered className="w-6 h-6 text-amber-700" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-accent">قيم التحدي</h2>
            <p className="text-sm text-muted-foreground">القيم التي تظهر في الملف الشخصي عند اختيار التحدي</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {challengeValues.map(val => (
            <div key={val} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2" data-testid={`challenge-${val}`}>
              <span className="font-bold text-amber-800">{val}</span>
              <button onClick={() => removeChallenge(val)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`remove-challenge-${val}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="number" value={newChallenge} onChange={e => setNewChallenge(e.target.value)}
            placeholder="أدخل قيمة جديدة..."
            className="flex-1 px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary"
            onKeyDown={e => e.key === 'Enter' && addChallenge()}
            data-testid="new-challenge-input"
          />
          <Button onClick={addChallenge} className="bg-primary hover:bg-primary/90" data-testid="add-challenge-btn">
            <Plus className="w-4 h-4 ml-1" /> إضافة
          </Button>
        </div>
      </Card>

      {/* Article Values */}
      <Card className="p-6 space-y-4" data-testid="articles-section">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 rounded-full bg-emerald-100">
            <BookOpen className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-accent">قائمة المقالات</h2>
            <p className="text-sm text-muted-foreground">المقالات التي تظهر عند اختيار "مقالة" في التقرير القرائي</p>
          </div>
        </div>

        {/* Existing auto-suggested titles info */}
        {existingTitles.length > 0 && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl" data-testid="existing-titles-info">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800">عناوين مقترحة تلقائياً ({existingTitles.length})</p>
                <p className="text-xs text-blue-600 mt-0.5">هذه العناوين مأخوذة تلقائياً من المقالات والتقارير السابقة</p>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {existingTitles.slice(0, 15).map((t, i) => (
                <span key={i} className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{t}</span>
              ))}
              {existingTitles.length > 15 && <span className="text-[11px] text-blue-500">+{existingTitles.length - 15} أخرى</span>}
            </div>
          </div>
        )}

        {/* Admin-managed articles */}
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {articleValues.length === 0 && <p className="text-muted-foreground text-sm py-4 text-center">لا توجد مقالات مضافة يدوياً بعد</p>}
          {articleValues.map((val, idx) => (
            <div key={idx} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5" data-testid={`article-item-${idx}`}>
              {editingIdx === idx ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text" value={editValue} onChange={e => setEditValue(e.target.value)}
                    className="flex-1 px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    onKeyDown={e => { if (e.key === 'Enter') saveEditArticle(); if (e.key === 'Escape') cancelEditArticle(); }}
                    autoFocus
                    data-testid={`edit-article-input-${idx}`}
                  />
                  <button onClick={saveEditArticle} className="text-green-500 hover:text-green-700 transition-colors" data-testid={`save-article-edit-${idx}`}>
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={cancelEditArticle} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid={`cancel-article-edit-${idx}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <span className="text-sm text-emerald-800">{val}</span>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => startEditArticle(idx)} className="text-blue-400 hover:text-blue-600 transition-colors" data-testid={`edit-article-${idx}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeArticle(val)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`remove-article-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="text" value={newArticle} onChange={e => setNewArticle(e.target.value)}
            placeholder="أدخل اسم المقالة..."
            className="flex-1 px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary"
            onKeyDown={e => e.key === 'Enter' && addArticle()}
            data-testid="new-article-input"
          />
          <Button onClick={addArticle} className="bg-primary hover:bg-primary/90" data-testid="add-article-btn">
            <Plus className="w-4 h-4 ml-1" /> إضافة
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AdminDropdownPage;
